/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   รายละเอียด RC   /support/rc/view/:code
   ซ้าย: เรื่องที่แจ้ง + ไฟล์ + ประวัติ   ขวา: อัปเดตสถานะ / ตอบลูกค้า / ยอดเงิน
============================================================ */
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Descriptions, Flex, Form, Input, InputNumber, Row, Select, Space, Spin, Tabs, Typography, message } from "antd";
import { SendOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ButtonBack } from "../../../components/button";
import FileList from "../../../components/ak/FileList";
import FilePicker from "../../../components/ak/FilePicker";
import RcTimeline from "../../../components/ak/RcTimeline";
import { RC_STATUS, RcStatusTag, RcTypeTag, costText } from "../../../components/ak/rc";
import { RcService, apiError } from "../../../service/Ak.service";
import { openFile } from "../../../utils/fileOpen";
import { usePermission } from "../../../store/context/permission-context";

export default function RcView() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canEdit = can("support_rc", "edit");
  const [rc, setRc] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const apply = (d) => {
    setRc(d);
    form.setFieldsValue({ status: d.status, cost_change: d.cost_change === null ? null : Number(d.cost_change), note: "" });
  };
  const load = () =>
    RcService.get(code)
      .then((r) => apply(r.data.data))
      .catch((e) => message.error(apiError(e, "โหลดไม่สำเร็จ")));

  useEffect(() => {
    load();
  }, [code]);

  const save = async () => {
    const v = form.getFieldsValue();
    setSaving(true);
    try {
      const r = await RcService.update({ rc_code: code, status: v.status, note: v.note || undefined, cost_change: v.cost_change ?? null });
      apply(r.data.data);
      message.success("บันทึกแล้ว — ลูกค้าเห็นสถานะ/ข้อความนี้ใน /portal");
    } catch (e) {
      message.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const r = await RcService.addNote({ rc_code: code, note: note.trim() || undefined }, files);
      apply(r.data.data);
      setNote("");
      setFiles([]);
      message.success("แนบแล้ว");
    } catch (e) {
      message.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  if (!rc) return <Flex justify="center" style={{ padding: 80 }}><Spin /></Flex>;
  const open = (f, inline) => openFile(() => RcService.blob(f.file_id, inline), f, inline).catch((e) => message.error(e.message));
  const cusFiles = rc.files.filter((f) => f.uploaded_type === "customer");
  const staffFiles = rc.files.filter((f) => f.uploaded_type !== "customer");

  return (
    <div>
      <Flex justify="space-between" align="center" className="mb-3" wrap="wrap" gap={8}>
        <div>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            {rc.title} <RcStatusTag value={rc.status} />
          </Typography.Title>
          <Typography.Text type="secondary">
            {rc.rc_code} · <RcTypeTag value={rc.rc_type} /> ·{" "}
            <Typography.Link onClick={() => navigate(`/support/project/view/${rc.project_code}`)}>{rc.project_name}</Typography.Link>
          </Typography.Text>
        </div>
        <ButtonBack target="/support/rc" />
      </Flex>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={15}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Card className="ak-card" title="เรื่องที่แจ้ง">
              <Descriptions size="small" column={{ xs: 1, md: 2 }} bordered>
                <Descriptions.Item label="ลูกค้า">{[rc.prename, rc.cus_name].filter(Boolean).join(" ")}</Descriptions.Item>
                <Descriptions.Item label="เบอร์โทร">{rc.cus_tel ? <a href={`tel:${rc.cus_tel}`}>{rc.cus_tel}</a> : "-"}</Descriptions.Item>
                <Descriptions.Item label="แจ้งโดย">{rc.created_by_type === "customer" ? "ลูกค้า (portal)" : "เจ้าหน้าที่บันทึกแทน"}</Descriptions.Item>
                <Descriptions.Item label="วันที่แจ้ง">{dayjs(rc.created_date).format("DD/MM/YYYY HH:mm")}</Descriptions.Item>
                <Descriptions.Item label="ยอดเงิน">{costText(rc.cost_change) || "-"}</Descriptions.Item>
                <Descriptions.Item label="อัปเดตล่าสุด">{rc.updated_date ? dayjs(rc.updated_date).format("DD/MM/YYYY HH:mm") : "-"}</Descriptions.Item>
                <Descriptions.Item label="รายละเอียด" span={2}>
                  <div style={{ whiteSpace: "pre-wrap" }}>{rc.detail || "-"}</div>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card className="ak-card" title={`ไฟล์แนบ (${rc.files.length})`}>
              <Tabs
                size="small"
                items={[
                  { key: "c", label: `จากลูกค้า (${cusFiles.length})`, children: <FileList files={cusFiles} onOpen={open} /> },
                  { key: "s", label: `จากเจ้าหน้าที่ (${staffFiles.length})`, children: <FileList files={staffFiles} onOpen={open} /> },
                ]}
              />
            </Card>

            <Card className="ak-card" title="ประวัติ / การพูดคุย">
              <RcTimeline logs={rc.logs} viewer="staff" />
            </Card>
          </Space>
        </Col>

        <Col xs={24} lg={9}>
          {canEdit ? (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Card className="ak-card" title="อัปเดตสถานะ / ตอบลูกค้า">
                <Form form={form} layout="vertical">
                  <Form.Item name="status" label="สถานะ">
                    <Select options={RC_STATUS.map((s) => ({ value: s, label: <RcStatusTag value={s} /> }))} />
                  </Form.Item>
                  <Form.Item name="cost_change" label="ยอดเงินที่เปลี่ยน (บาท)" extra="บวก = งานเพิ่ม (ลูกค้าจ่ายเพิ่ม) · ลบ = งานลด">
                    <InputNumber style={{ width: "100%" }} step={1000} formatter={(v) => (v === undefined || v === null || v === "" ? "" : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","))} parser={(v) => v.replace(/,/g, "")} />
                  </Form.Item>
                  <Form.Item name="note" label="ข้อความถึงลูกค้า">
                    <Input.TextArea rows={4} maxLength={4000} placeholder="เช่น รับเรื่องแล้ว ทีมจะประเมินราคาและแจ้งกลับภายใน 3 วัน" />
                  </Form.Item>
                  <Button type="primary" block loading={saving} onClick={save}>
                    บันทึก
                  </Button>
                </Form>
                {rc.staff_reply && (
                  <Alert
                    className="mt-3"
                    type="info"
                    message={`ตอบล่าสุด ${rc.replied_date ? dayjs(rc.replied_date).format("DD/MM/YYYY HH:mm") : ""}`}
                    description={<div style={{ whiteSpace: "pre-wrap" }}>{rc.staff_reply}</div>}
                  />
                )}
              </Card>

              <Card className="ak-card" title="แนบไฟล์ให้ลูกค้า">
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} placeholder="ข้อความประกอบ (ไม่บังคับ) เช่น แบบที่แก้แล้ว / ใบเสนอราคางานเพิ่ม" />
                  <FilePicker value={files} onChange={setFiles} mode="button" />
                  <Button icon={<SendOutlined />} block loading={sending} disabled={!files.length && !note.trim()} onClick={send}>
                    ส่ง
                  </Button>
                </Space>
              </Card>
            </Space>
          ) : (
            <Card className="ak-card">
              <Typography.Text type="secondary">ดูได้อย่างเดียว — ตำแหน่งนี้ไม่มีสิทธิ์อัปเดต RC</Typography.Text>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
